import { EARLY_FETCHES_WINDOW_KEY } from "@lib/earlyRoundFetches";

// Adoption side of the early-fetch handoff (see lib/earlyRoundFetches.ts for
// the full mechanism): on round routes the served HTML shell fires the
// round-gating GET requests from an inline script — in parallel with bundle
// download/eval, ~250ms before this code can run — and stashes them on
// `window.__earlyFetches` as `{ createdAt, entries }`, with the in-flight
// promises keyed by exact request URL. This wrapper lets the tRPC links adopt
// a matching in-flight response instead of issuing a duplicate request. (The
// inline script also drains each body via a clone as it arrives — see
// server/lib/appShell.ts — so the Response handed over here is fully readable
// even though its bytes already left the network.)
//
// Like trpcLinks.ts, this module must stay a dependency-light runtime leaf so
// the link-routing unit tests can import the link stack without dragging in
// the full client module graph.

type EarlyFetches = {
  createdAt: number;
  entries: Map<string, Promise<Response>>;
};

// Adoption is a BOOT bridge, not a cache: the shell's fetches are only
// byte-equivalent stand-ins for requests the entry bundle would have made
// itself moments later. An entry the boot never consumed (round not
// cache-first, boot under impersonation/bearer headers, logged-out 401s)
// would otherwise sit in the map for the document's lifetime — and a much
// later in-document round load (e.g. the round turns cache-first mid-session
// via its learned aggregate size) would adopt a stale response, missing any
// intervening version/membership/access change. So adoption is refused, and
// the map dropped for GC, once this window after the shell fired has passed.
// 30s comfortably covers bundle download+eval on a cold slow device (the
// consumers all run immediately after boot) and is within what cache-first
// loading already tolerates as staleness (the socket catches up gaps).
const ADOPTION_WINDOW_MS = 30_000;

function readEarlyFetches(): EarlyFetches | null {
  const holder = globalThis as Record<string, unknown>;
  const value = holder[EARLY_FETCHES_WINDOW_KEY];
  if (
    typeof value !== "object" ||
    value == null ||
    typeof (value as EarlyFetches).createdAt !== "number" ||
    !((value as EarlyFetches).entries instanceof Map)
  ) {
    return null;
  }
  const earlyFetches = value as EarlyFetches;
  if (Date.now() - earlyFetches.createdAt > ADOPTION_WINDOW_MS) {
    delete holder[EARLY_FETCHES_WINDOW_KEY];
    return null;
  }
  return earlyFetches;
}

// The inline script fires its requests with no headers beyond the session
// cookie. A request carrying either of these headers resolves a DIFFERENT
// viewer or identity than the early fetch did (impersonation on web, bearer
// auth on native), so adopting the early response would silently serve the
// wrong user's payload. Never adopt for those.
const RESPONSE_CHANGING_HEADERS = ["authorization", "x-acting-as-user-id"];

function hasResponseChangingHeader(headers: HeadersInit | undefined): boolean {
  if (headers == null) return false;
  const normalized = new Headers(headers);
  return RESPONSE_CHANGING_HEADERS.some((name) => normalized.has(name));
}

/**
 * The in-flight early fetch matching this request, or null when the request
 * must go over the network as normal. Adoption is exactly-once: the entry is
 * removed from the map, so a second request for the same URL (refetch,
 * invalidation) fetches fresh.
 */
export function consumeEarlyFetch(
  input: Parameters<typeof fetch>[0],
  init?: RequestInit,
): Promise<Response> | null {
  try {
    // Only bare string URLs are matched — the tRPC links pass the URL they
    // built as a string, and matching a Request/URL object would need
    // origin-relative normalization for no current caller.
    if (typeof input !== "string") return null;
    if (init?.method != null && init.method.toUpperCase() !== "GET") {
      return null;
    }
    if (hasResponseChangingHeader(init?.headers)) return null;
    const earlyFetches = readEarlyFetches();
    const inFlight = earlyFetches?.entries.get(input);
    if (earlyFetches == null || inFlight == null) return null;
    earlyFetches.entries.delete(input);
    // A rejected early fetch (e.g. it lost a race with a network blip) or an
    // entry that isn't a Response falls through to a normal fetch so behavior
    // degrades to exactly today's path.
    return inFlight.then(
      (response) =>
        response instanceof Response ? response : fetch(input, init),
      () => fetch(input, init),
    );
  } catch {
    // Adoption is an optimization only — any surprise here must not take down
    // the request itself.
    return null;
  }
}

/**
 * Drop-in `fetch` for the tRPC links: adopt a matching early fetch when one
 * is in flight, otherwise plain `fetch`. (Typed as a plain function rather
 * than `typeof fetch` — Bun's fetch type adds a `preconnect` static that a
 * wrapper neither has nor needs.)
 */
export const fetchWithEarlyFetches = (
  input: Parameters<typeof fetch>[0],
  init?: RequestInit,
): Promise<Response> => {
  return consumeEarlyFetch(input, init) ?? fetch(input, init);
};
